import React from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Wallet, Clock, Calendar, Users, Zap } from "lucide-react";
import { rupiah, formatDate } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { StarRating, ReputationBadge } from "@/components/StarRating";

export function JobCard({ job, index = 0, showStatus = false, footer }) {
  const navigate = useNavigate();
  return (
    <div
      data-testid={`job-card-${job.id}`}
      onClick={() => navigate(`/pekerjaan/${job.id}`)}
      className="bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-[0_8px_30px_rgba(15,23,42,0.10)] hover:border-slate-300 transition-all duration-200 cursor-pointer flex flex-col gap-3 animate-fade-up"
      style={{ animationDelay: `${index * 40}ms` }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 mb-1.5">
            {job.featured && (
              <span data-testid="featured-badge" className="inline-flex items-center gap-1 bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full text-xs font-bold">
                <Zap size={11} className="fill-amber-500 text-amber-500" /> Mendesak
              </span>
            )}
            <span className="inline-block bg-slate-100 text-slate-600 px-2.5 py-0.5 rounded-full text-xs font-medium">
              {job.category?.name || "Lainnya"}
            </span>
          </div>
          <h3 className="font-bold text-slate-900 text-base leading-snug truncate">{job.title}</h3>
        </div>
        {showStatus && <StatusBadge status={job.status} />}
      </div>

      <div className="flex items-center gap-1.5 text-sm text-slate-500">
        <MapPin size={15} className="text-blue-600 shrink-0" />
        <span className="truncate">{job.city}</span>
        {job.distance_km != null && (
          <span className="text-slate-400">· {job.distance_km} km</span>
        )}
      </div>

      <div className="flex items-center flex-wrap gap-x-4 gap-y-1.5 text-sm">
        <span className="inline-flex items-center gap-1.5 font-bold text-slate-900">
          <Wallet size={15} className="text-emerald-600" /> {rupiah(job.payment)}
        </span>
        <span className="inline-flex items-center gap-1.5 text-slate-500">
          <Clock size={15} /> {job.duration}
        </span>
        <span className="inline-flex items-center gap-1.5 text-slate-500">
          <Calendar size={15} /> {formatDate(job.date)}
        </span>
      </div>

      <div className="flex items-center justify-between pt-1 border-t border-slate-100 mt-1">
        <div className="flex items-center gap-3 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1">
            <Users size={13} /> {job.accepted_count ?? 0}/{job.workers_needed}
          </span>
          {job.employer?.rating_count > 0 && (
            <StarRating value={job.employer.rating_average} count={job.employer.rating_count} size={12} />
          )}
          {job.employer?.reputation && <ReputationBadge level={job.employer.reputation} />}
        </div>
        <span className="text-blue-600 font-semibold text-sm">Lihat Detail →</span>
      </div>
      {footer}
    </div>
  );
}
